import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useStore from "../../store/useStore";
import Loader from "../../components/Loader";
import { FaShoppingBag, FaHeart, FaLock } from "react-icons/fa";
import toast from "react-hot-toast";
import usePincodeCheck from "../../utils/usePincodeCheck";

import CartAddressCard from "./components/CartAddressCard";
import CartDeliveryOptions from "./components/CartDeliveryOptions";
import CartProductsList from "./components/CartProductsList";
import CartBillingSummary from "./components/CartBillingSummary";
import CartCouponPanel from "./components/CartCouponPanel";
import CartStickyFooter from "./components/CartStickyFooter";

const cartStockKey = (item) =>
  `${item.product_id ?? item.product?.product_id ?? item.id}-${item.product_variant_id ?? item.variant_id ?? "base"}`;

const isExplicitFalse = (value) => value === false || value === 0 || value === "0" || value === "false";
const isExplicitTrue = (value) => value === true || value === 1 || value === "1" || value === "true";
const isUnavailableStockResult = (item) => {
  const status = String(item.stock_status || "").toUpperCase();
  const available = item.available_stock == null ? null : Number(item.available_stock);
  if (
    isExplicitTrue(item.is_out_of_stock) ||
    isExplicitFalse(item.delivery_available) ||
    ["OUT_OF_STOCK", "NOT_SERVICEABLE", "UNAVAILABLE", "SOLD_OUT"].includes(status)
  ) {
    return true;
  }

  // Limited/insufficient labels with a positive quantity still allow checkout.
  if (available != null && available > 0) return false;
  if (available === 0 || isExplicitFalse(item.in_stock)) return true;
  return status !== "" && !["IN_STOCK", "AVAILABLE", "LIMITED_STOCK", "LOW_STOCK", "INSUFFICIENT_STOCK"].includes(status);
};

const CartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};

  const {
    cart,
    cart_count,
    cart_meta,
    cart_loading,
    cart_error,
    fetchCart,
    incrementItem,
    decrementItem,
    removeFromCart,
    clearCart,
    addToWishlist,
    isAuthenticated,
    openAuthModal,
    is_guest_cart,
    mergeGuestCartOnLogin,
    user_addresses,
    fetchUserAddresses,
    deleteAddress,
    updateAddress,
    addresses_loading,
    setSelectedAddressId: setStoreSelectedAddressId,
    checkCartStock,
  } = useStore();

  const [deliveryOption, setDeliveryOption] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(
    locationState.selectedTimeSlot || ""
  );
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState(
    locationState.selectedTimeSlotId || null
  );
  const [selectedAddressId, setSelectedAddressId] = useState(
    locationState.selectedAddressId || null
  );
  const [checkingStock, setCheckingStock] = useState(false);
  const [stockAvailability, setStockAvailability] = useState({});
  const [stockCheckLoading, setStockCheckLoading] = useState(false);
  const [stockCheckError, setStockCheckError] = useState(false);

  const handleSelectDeliveryOption = (typeLower, feeNum) => {
    setDeliveryOption(typeLower);
  };

  const handleSelectTimeSlot = (slotName, slotId) => {
    setSelectedTimeSlot(slotName);
    if (slotId != null) {
      setSelectedTimeSlotId(slotId);
    }
  };

  // Use API values directly — no local recalculation
  // cart_meta.delivery_fee and cart_meta.total_amount come straight from the API response
  const activeDeliveryFee = parseFloat(cart_meta?.delivery_fee ?? 0);
  const activeTotalAmount = parseFloat(cart_meta?.total_amount ?? 0);
  const selectedDeliveryOption = deliveryOption;

  const hasMountedRef = useRef(false);
  const hasInitializedAddressRef = useRef(false);
  const hasManuallySelectedAddressRef = useRef(false);
  // Track the last delivery type sent to the API so we avoid duplicate fetches.
  const lastFetchedDeliveryTypeRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      if (is_guest_cart) {
        mergeGuestCartOnLogin();
      } else {
        fetchCart(null, deliveryOption || null, selectedAddressId);
      }
      if (typeof fetchUserAddresses === "function") {
        fetchUserAddresses();
      }
    } else {
      fetchCart(null, deliveryOption || null, selectedAddressId);
    }
    lastFetchedDeliveryTypeRef.current = deliveryOption;
    hasMountedRef.current = true;
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch cart when the user switches delivery type.
  useEffect(() => {
    if (!hasMountedRef.current) return;
    if (lastFetchedDeliveryTypeRef.current === deliveryOption) return;
    if (isAuthenticated && !is_guest_cart) {
      lastFetchedDeliveryTypeRef.current = deliveryOption;
      fetchCart(activePincode || null, deliveryOption, selectedAddressId);
    }
  }, [deliveryOption]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user_addresses || user_addresses.length === 0) return;
    const routeAddress = locationState.selectedAddressId
      ? user_addresses.find((address) => String(address.address_id ?? address.id) === String(locationState.selectedAddressId))
      : null;
    const currentAddress = selectedAddressId
      ? user_addresses.find((address) => String(address.address_id ?? address.id) === String(selectedAddressId))
      : null;

    if (hasInitializedAddressRef.current && (hasManuallySelectedAddressRef.current || currentAddress)) {
      return;
    }

    const initialAddress = routeAddress || user_addresses.find((address) => address.is_default) || user_addresses[0];
    if (!initialAddress) return;

    const initialAddressId = initialAddress.address_id ?? initialAddress.id;
    hasInitializedAddressRef.current = true;
    setStoreSelectedAddressId?.(initialAddressId);
    if (String(selectedAddressId) !== String(initialAddressId)) {
      setSelectedAddressId(initialAddressId);
      if (isAuthenticated && !is_guest_cart) {
        fetchCart(initialAddress.pincode || null, deliveryOption || null, initialAddressId);
      }
    }
  }, [user_addresses, selectedAddressId]); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultAddress =
    isAuthenticated && user_addresses && user_addresses.length > 0
      ? user_addresses.find((a) => String(a.address_id ?? a.id) === String(selectedAddressId)) ||
        user_addresses.find((a) => a.is_default) ||
        user_addresses[0]
      : null;

  const activePincode = defaultAddress?.pincode ? String(defaultAddress.pincode).trim() : "";
  const hasUnavailableCartItems = Object.values(stockAvailability).some((item) => item.is_unavailable);
  const {
    status: pincodeStatus,
    message: pincodeMessage,
    data: pincodeData,
  } = usePincodeCheck(activePincode);

  // Refresh per-item availability whenever the cart or selected delivery address changes.
  // This keeps the badges in sync with the same address that checkout will validate.
  useEffect(() => {
    if (cart_loading) {
      setStockAvailability({});
      setStockCheckLoading(cart.length > 0 && isAuthenticated && !is_guest_cart);
      return undefined;
    }

    if (!isAuthenticated || is_guest_cart || !selectedAddressId || !activePincode || cart.length === 0) {
      setStockAvailability({});
      setStockCheckLoading(false);
      setStockCheckError(false);
      return undefined;
    }

    let active = true;
    setStockAvailability({});
    setStockCheckLoading(true);
    setStockCheckError(false);

    checkCartStock({
      address_id: selectedAddressId,
      pincode: activePincode,
      cart_items: cart,
    })
      .then((response) => {
        if (!active) return;
        const data = response?.data || {};
        const checkedItems = data.cart_items || data.items || [];
        const nextAvailability = {};

        checkedItems.forEach((checkedItem) => {
          const status = String(checkedItem.stock_status || "").toUpperCase();
          const requestedQuantity = Number(
            checkedItem.requested_quantity ?? checkedItem.quantity ??
            cart.find((item) => String(item.product_id ?? item.id) === String(checkedItem.product_id))?.quantity ?? 1
          );
          const availableStock = checkedItem.available_stock == null
            ? null
            : Number(checkedItem.available_stock);
          const isUnavailable = isUnavailableStockResult({
            ...checkedItem,
            available_stock: availableStock,
            requested_quantity: requestedQuantity,
          });
          const key = cartStockKey(checkedItem);

          nextAvailability[key] = {
            stock_status: status || (isUnavailable ? "OUT_OF_STOCK" : "IN_STOCK"),
            stock_message: checkedItem.stock_message || checkedItem.message || null,
            available_stock: availableStock,
            is_unavailable: isUnavailable,
          };
        });

        if (
          checkedItems.length === 0 &&
          (data.has_out_of_stock_items === true || data.can_proceed_to_checkout === false)
        ) {
          cart.forEach((item) => {
            nextAvailability[cartStockKey(item)] = {
              stock_status: "OUT_OF_STOCK",
              stock_message: "Availability needs attention for this address",
              available_stock: null,
              is_unavailable: true,
            };
          });
        }

        setStockAvailability(nextAvailability);
      })
      .catch(() => {
        if (active) setStockCheckError(true);
      })
      .finally(() => {
        if (active) setStockCheckLoading(false);
      });

    return () => {
      active = false;
    };
  }, [cart, cart_loading, selectedAddressId, activePincode, isAuthenticated, is_guest_cart, checkCartStock]);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (!activePincode) {
      toast.error("Please add or select a delivery address.");
      return;
    }
    if (pincodeStatus === "checking") {
      toast("Checking delivery availability for your pincode, please wait…", { icon: "⏳" });
      return;
    }
    if (pincodeStatus === "not_serviceable" || pincodeStatus === "error") {
      toast.error(
        pincodeMessage ||
          `Delivery is not available for pincode ${activePincode}. Please change your delivery address.`
      );
      return;
    }
    if (selectedDeliveryOption === "scheduled" && !selectedTimeSlot) {
      toast.error("Please select a preferred time slot for scheduled delivery.");
      return;
    }
    if (!selectedDeliveryOption) {
      toast.error("Please select a delivery option to proceed.");
      return;
    }

    // ── Check Cart Stock API ──
    try {
      setCheckingStock(true);
      const stockRes = await checkCartStock({
        pincode: activePincode,
        address_id: selectedAddressId,
        cart_items: cart,
      });

      const stockData = stockRes?.data || {};

      if (stockData.is_pincode_serviceable === false) {
        toast.error(
          `Delivery is not available for pincode ${activePincode}. Please change your delivery address.`,
          { id: "cart-pincode-not-serviceable" }
        );
        setCheckingStock(false);
        return;
      }

      const checkedStockItems = stockData.cart_items || stockData.items || [];
      const unavailableItems = checkedStockItems.filter(isUnavailableStockResult);
      const hasUnresolvedGlobalStockBlock =
        checkedStockItems.length === 0 &&
        (stockData.can_proceed_to_checkout === false || stockData.has_out_of_stock_items === true);

      if (unavailableItems.length > 0 || hasUnresolvedGlobalStockBlock) {
        const oosItems = unavailableItems;

        if (oosItems.length > 0) {
          const oosMessages = oosItems.map((ci) => {
            const matchCartItem = cart.find(
              (it) => String(it.product_id ?? it.id) === String(ci.product_id)
            );
            const itemName = matchCartItem?.name || `Product #${ci.product_id}`;
            if (String(ci.stock_status || "").toUpperCase() === "NOT_SERVICEABLE") {
              return `${itemName} is not serviceable at this pincode`;
            }
            if (ci.available_stock !== undefined && ci.available_stock > 0) {
              return `${itemName}: only ${ci.available_stock} in stock (requested ${ci.requested_quantity})`;
            }
            return `${itemName} is out of stock`;
          });

          toast.custom(
            (t) => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  background: "#fff",
                  border: "1.5px solid #fecaca",
                  borderRadius: "14px",
                  padding: "14px 16px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
                  maxWidth: "min(92vw, 400px)",
                  opacity: t.visible ? 1 : 0,
                  transform: t.visible ? "translateY(0) scale(1)" : "translateY(-8px) scale(0.97)",
                  transition: "all 0.25s ease",
                }}
              >
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "#fee2e2",
                      color: "#dc2626",
                      fontSize: "16px",
                      flexShrink: 0,
                    }}
                  >
                    ⚠️
                  </span>
                  <div>
                    <p style={{ margin: 0, fontSize: "13px", fontWeight: "800", color: "#111" }}>
                      Items Unavailable
                    </p>
                    <p style={{ margin: 0, fontSize: "11px", color: "#6b7280" }}>
                      Please remove these items to proceed
                    </p>
                  </div>
                </div>

                {/* Item list */}
                <ul
                  style={{
                    margin: "2px 0 0 0",
                    padding: "0 0 0 4px",
                    listStyle: "none",
                    maxHeight: "140px",
                    overflowY: oosMessages.length > 4 ? "auto" : "visible",
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  {oosMessages.map((msg, idx) => (
                    <li
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "6px",
                        fontSize: "12px",
                        color: "#374151",
                        lineHeight: "1.4",
                      }}
                    >
                      <span style={{ color: "#dc2626", fontWeight: "700", flexShrink: 0 }}>•</span>
                      <span>{msg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ),
            { id: "cart-oos-items", duration: 6000 }
          );
        } else {
          toast.error(
            stockRes?.message || "Some items in your cart cannot be delivered. Please update your cart.",
            { id: "cart-cannot-deliver" }
          );
        }
        setCheckingStock(false);
        return;
      }
    } catch (err) {
      toast.error(err.message || "Failed to verify stock availability. Please try again.", { id: "cart-stock-check-error" });
      setCheckingStock(false);
      return;
    } finally {
      setCheckingStock(false);
    }

    navigate("/checkout", {
      state: {
        deliveryOption: selectedDeliveryOption,
        selectedTimeSlot,
        selectedTimeSlotId,
        selectedDeliveryFee: activeDeliveryFee,
        selectedAddressId,
      },
    });
  };

  const handleSelectAddress = (addr) => {
    const id = addr.address_id ?? addr.id;
    hasManuallySelectedAddressRef.current = true;
    setSelectedAddressId(id);
    setStoreSelectedAddressId?.(id);
    setStockAvailability({});
    setStockCheckLoading(true);
    setStockCheckError(false);
    if (addr.pincode) fetchCart(addr.pincode, selectedDeliveryOption, id);
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await deleteAddress(id);
      if (String(selectedAddressId) === String(id)) {
        hasManuallySelectedAddressRef.current = false;
        setSelectedAddressId(null);
        setStoreSelectedAddressId?.(null);
      }
      toast.success("Address deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete address");
    }
  };

  const handleEditAddress = async (editingAddr, formData) => {
    const id = editingAddr.address_id ?? editingAddr.id;
    try {
      await updateAddress(id, formData);
      toast.success("Address updated!");
      await fetchUserAddresses();
    } catch (err) {
      toast.error(err.message || "Failed to update address");
    }
  };

  const handleAddNewAddress = (savedAddr) => {
    const fresh = useStore.getState().user_addresses;
    const savedId = savedAddr?.address_id ?? savedAddr?.id;
    const newlyAdded = savedId
      ? fresh.find((a) => String(a.address_id ?? a.id) === String(savedId)) ?? fresh[fresh.length - 1]
      : fresh[fresh.length - 1];
    if (newlyAdded) handleSelectAddress(newlyAdded);
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
      toast.success("Cart cleared");
    } catch {
      toast.error("Failed to clear cart");
    }
  };

  const handleMoveToWishlist = async (item) => {
    if (!isAuthenticated) {
      toast("Please login to save items to wishlist", { icon: <FaLock /> });
      openAuthModal();
      return;
    }
    const pid = item.product_id ?? item.id;
    try {
      const alreadyInWishlist = useStore.getState().isInWishlist(pid);
      if (alreadyInWishlist) {
        // Already saved — just remove from cart, no duplicate API call
        await removeFromCart(item);
        toast.success("Removed from cart (already in wishlist)", {
          icon: <FaHeart className="text-rose-500" />,
        });
      } else {
        // Not in wishlist yet — add it, then remove from cart
        const added = await addToWishlist(pid);
        if (added !== false) {
          await removeFromCart(item);
          toast.success("Moved to wishlist", {
            icon: <FaHeart className="text-rose-500" />,
          });
        }
      }
    } catch {
      toast.error("Failed to move item to wishlist");
    }
  };

  if (cart_loading && cart.length === 0) {
    return (
      <Loader
        fullScreen
        label="Loading cart"
        sublabel="Syncing your cart items and totals..."
      />
    );
  }

  if (!cart_loading && cart.length === 0) {
    return (
      <div className="min-h-[70vh] bg-gray-50 py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center shadow-sm border border-gray-100">
          <div className="w-20 h-20 bg-emerald-100 text-[#046a38] rounded-full flex items-center justify-center mx-auto mb-4">
            <FaShoppingBag className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Your cart is empty
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Looks like you haven't added any products to your cart yet.
          </p>
          <button
            onClick={() => navigate("/products")}
            className="w-full bg-[#046a38] text-white font-bold py-3.5 px-6 rounded-xl hover:bg-[#03522b] transition-all shadow-md active:scale-95 text-sm"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 pb-28 pt-4 sm:pt-6 font-sans">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">

        {/* Step Tracker */}
        <div className="mb-4 sm:mb-6 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center text-xs font-semibold sm:text-sm">
            <div className="relative flex flex-1 items-center justify-center gap-1.5 sm:gap-2 bg-[#046a38] py-2.5 sm:py-3 text-white pr-4">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-[#046a38]">
                1
              </span>
              <span className="truncate">Cart / Review</span>
              <div className="absolute -right-3 top-0 z-10 h-0 w-0 border-y-[20px] sm:border-y-[22px] border-l-[12px] border-y-transparent border-l-[#046a38]" />
            </div>

            <div className="relative flex flex-1 items-center justify-center gap-1.5 sm:gap-2 bg-gray-100 py-2.5 sm:py-3 text-gray-600 px-4">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-300 text-[11px] font-bold text-gray-700">
                2
              </span>
              <span className="truncate">Checkout</span>
              <div className="absolute -right-3 top-0 z-10 h-0 w-0 border-y-[20px] sm:border-y-[22px] border-l-[12px] border-y-transparent border-l-gray-100" />
            </div>

            <div className="flex flex-1 items-center justify-center gap-1.5 sm:gap-2 bg-gray-100 py-2.5 sm:py-3 text-gray-600 pl-4">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-300 text-[11px] font-bold text-gray-700">
                3
              </span>
              <span className="truncate">Order Placed</span>
            </div>
          </div>
        </div>

        {/* Error alert */}
        {cart_error && (
          <div className="mb-4 rounded-xl bg-red-50 p-3 text-xs sm:text-sm text-red-600 border border-red-200">
            {cart_error}
          </div>
        )}

        <div className="space-y-4">
          <CartAddressCard
            defaultAddress={defaultAddress}
            userAddresses={user_addresses}
            selectedAddressId={selectedAddressId}
            onSelectAddress={handleSelectAddress}
            onDeleteAddress={handleDeleteAddress}
            onEditAddress={handleEditAddress}
            onAddNewAddress={handleAddNewAddress}
            isAuthenticated={isAuthenticated}
            onRequireAuth={openAuthModal}
            addressesLoading={addresses_loading}
            pincodeStatus={pincodeStatus}
            pincodeMessage={pincodeMessage}
            pincodeData={pincodeData}
          />

          <CartProductsList
            cart={cart}
            cartCount={cart_count}
            cartLoading={cart_loading}
            stockAvailability={stockAvailability}
            stockCheckLoading={stockCheckLoading}
            stockCheckError={stockCheckError}
            onIncrement={incrementItem}
            onDecrement={decrementItem}
            onRemove={removeFromCart}
            onMoveToWishlist={handleMoveToWishlist}
            onClearCart={handleClearCart}
          />

          <CartDeliveryOptions
            deliveryOption={selectedDeliveryOption}
            onSelectDeliveryOption={handleSelectDeliveryOption}
            selectedTimeSlot={selectedTimeSlot}
            onSelectTimeSlot={handleSelectTimeSlot}
            deliveryOptions={cart_meta?.delivery_options || []}
            deliveryFee={activeDeliveryFee}
          />

          <CartCouponPanel
            subtotal={cart_meta?.subtotal ?? 0}
            deliveryType={selectedDeliveryOption}
            addressId={selectedAddressId}
            isAuthenticated={isAuthenticated}
            onRequireAuth={openAuthModal}
            initialCode={locationState.couponCode}
          />

          <CartBillingSummary
            subtotal={cart_meta?.subtotal ?? 0}
            taxAmount={cart_meta?.tax_amount ?? 0}
            deliveryFee={activeDeliveryFee}
            discountAmount={cart_meta?.discount_amount ?? 0}
            appliedCoupon={cart_meta?.applied_coupon}
            totalAmount={activeTotalAmount}
            freeDeliveryMinAmount={cart_meta?.free_delivery_min_amount ?? 0}
            amountNeededForFreeDelivery={cart_meta?.amount_needed_for_free_delivery ?? 0}
            appliedLocation={cart_meta?.applied_location ?? null}
            appliedPincode={cart_meta?.applied_pincode ?? null}
            totalItems={cart_meta?.total_items ?? 0}
            totalQuantity={cart_meta?.total_quantity ?? 0}
          />
        </div>

        <CartStickyFooter
          grandTotal={activeTotalAmount}
          cartCount={cart_meta?.total_items ?? cart_count}
          onCheckout={handleCheckout}
          loading={checkingStock || stockCheckLoading}
          checkoutBlocked={hasUnavailableCartItems}
          isAuthenticated={isAuthenticated}
        />

      </div>
    </div>
  );
};

export default CartPage;
